import styled from "@emotion/styled";
import React, { SyntheticEvent, useMemo } from "react";
import { Dialog } from "../../components/dialogs/Dialog";
import { JobLinkedTable } from "../../components/table/JobLinkedTable";
import { KLAYO_COLORS } from "../../themes";

import { JobAttribute } from "@/interfaces/Job";
import { useNavigate } from "react-router-dom";
import { SectionLoader } from "../../components/SectionLoader";
import { useAppQuery } from "../../hooks/useAppQuery";
import { IJobJinked } from "../../interfaces/Competency";
import { useLocale } from "../../providers/LocaleProvider";

interface LinkedJobDialogProps {
  open: boolean;
  onClose: () => void;
  row?: {
    attributeDefinitionId?: string;
  };
  theme: any;
}

const LinkedJobDialog: React.FC<LinkedJobDialogProps> = ({ open, onClose, row, theme }) => {
  const navigate = useNavigate();
  const { translate } = useLocale();

  const { data: linkedJobRes, isFetching } = useAppQuery<IJobJinked>(
    `/AttributeDefinition/${row?.attributeDefinitionId}/linkedjobs`,
    {
      enabled: !!(open && row?.attributeDefinitionId)
    }
  );

  const linkedJobs = useMemo(() => {
    if (!linkedJobRes) return [];

    return linkedJobRes?.jobs || [];
  }, [linkedJobRes]);

  const handleJobClick = (job: JobAttribute, e: SyntheticEvent) => {
    navigate("/settings/jobs/edit/" + job.jobDefinitionId + "?navigateMode=competencies");

    onClose();
  };

  return (
    <StyledTrainingAttributesDialog
      className='klayo__training-attribute-dialog'
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      actions={[
        {
          label: translate("common.close"),
          variant: "filled",
          onClick: onClose
        }
      ]}
    >
      <h1>{translate("dialogs.linkedJobs")}</h1>
      <h2>{translate("dialogs.linkedJobsDescription")}</h2>

      {isFetching ? (
        <SectionLoader />
      ) : (
        <JobLinkedTable<JobAttribute>
          showFooterRowCount={true}
          pagination={true}
          rowHasAction={true}
          minHeight='56px'
          pageSize={5}
          showHeaderFromBreakpoint='md'
          theme={theme}
          dense={true}
          paginationMode='client'
          columns={[{ type: JobLinkedTable.columns.name }]}
          paper={false}
          onJobClick={handleJobClick}
          rows={linkedJobs}
          noRowsMessage={translate("common.noLinkedJobs")}
          translate={translate}
        />
      )}
    </StyledTrainingAttributesDialog>
  );
};

const StyledTrainingAttributesDialog = styled(Dialog)`
  &.klayo__training-attribute-dialog {
    h1 {
      color: ${KLAYO_COLORS.GreyGR900};
      font-family: Inter;
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 32px;
      letter-spacing: -0.5px;
      margin-top: 0px;
      margin-bottom: 32px;
    }

    h2 {
      color: ${KLAYO_COLORS.GreyGR900};
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      margin-top: 0px;
      margin-bottom: 24px;
    }

    .main-title {
      font-weight: 700;
    }

    .MuiDialogContent-root {
      padding: 40px 40px 0px 40px;
    }

    .MuiPaper-root {
      max-width: 640px;
    }
  }
`;

export default LinkedJobDialog;
